import { InputLabel, MenuItem } from '@mui/material';
import { memo } from 'react';
import { __MUNICIPALITY_API_URL__ } from 'shell/constants/environment';
import { HttpResponseList } from 'shell/models/http-response';
import { ForeignState } from 'shell/models/state';
import { PmiFormControl } from 'shell/components/shared/pmi-select/pmi-form-control';
import { PmiSelect } from 'shell/components/shared/pmi-select';
import { useHttpFetch } from 'shell/hooks/use-http-fetch';

interface ForeignStateListComponentProps {
  disabled?: boolean;
  value: string;
  onChange: (foreignState?: ForeignStateVM) => void;
  error?: boolean;
  label?: string;
  id?: string;
}

export interface ForeignStateVM {
  fiscalCode: string;
  istat: string;
  id: string;
  value: string;
  territory: string;
}

const ForeignStateListComponent = ({
  disabled,
  onChange,
  value,
  error,
  label = 'Stato Estero *',
  id = 'foreignState',
}: ForeignStateListComponentProps): React.ReactElement => {
  const [foreignStates] = useHttpFetch<HttpResponseList<ForeignState>, Array<ForeignStateVM>>(
    `${__MUNICIPALITY_API_URL__}/geo/stato`,
    {
      onBootstrap: true,
      options: {
        loading: true,
        errorCatching: true,
        method: 'GET',
      },
      transformer: (response) => {
        return response.data?.payload.list.map((item) => ({
          id: item.key.codice,
          value: item.esonimo,
          fiscalCode: item.belfiore,
          istat: item.codiceCittadinanza?.toString() || '',
          territory: item.codiceCittadinanza?.toString() || '',
        }));
      },
    }
  );

  const list = (foreignStates.data as Array<ForeignStateVM>) || [];

  const idTag = `${id}-${label}`;

  return (
    <PmiFormControl fullWidth>
      <InputLabel error={error} disabled={disabled} id={idTag}>
        {label}
      </InputLabel>
      <PmiSelect
        labelId={idTag}
        id={id}
        name={id}
        value={value}
        label={label}
        onChange={(event) => {
          const selectedValue = event.target.value;
          const foundElement = list.find((element) => element.id === selectedValue);
          onChange(foundElement);
        }}
        disabled={disabled}
        error={error}
      >
        <MenuItem value="">
          <em>Nessuno</em>
        </MenuItem>
        {list.map((item: ForeignStateVM) => (
          <MenuItem key={item.id} value={item.id}>
            {item.value}
          </MenuItem>
        ))}
      </PmiSelect>
    </PmiFormControl>
  );
};

ForeignStateListComponent.displayName = 'Foreign state item component';

const ForeignStateList = memo(ForeignStateListComponent);

export default ForeignStateList as typeof ForeignStateListComponent;
